import { Component, forwardRef, inject, Input, OnChanges, OnInit, Provider, SimpleChanges } from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR, UntypedFormGroup, Validators } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { MbsPopupType } from 'mbs-ui-kit';
import { PrePostActionsStepValue } from 'wizards/models/pre-post-actions-models';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import { StepBase } from 'wizards/steps/StepBase.class';

import {
  AlertComponent,
  CheckboxComponent,
  CheckboxDescription,
  FormGroupComponent,
  InputComponent,
  RadioComponent,
  SelectComponent
} from 'mbs-ui-kit';

import { I18NextPipe } from 'angular-i18next';

import { CommonModule } from '@angular/common';

import { ReactiveFormsModule } from '@angular/forms';

import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';

const PrePostActionsStepValueAccessor: Provider = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useExisting: forwardRef(() => PrePostActionsStepComponent),
  multi: true
};

@UntilDestroy()
@Component({
  selector: 'mbs-pre-post-actions-step',
  templateUrl: './pre-post-actions-step.component.html',
  providers: [PrePostActionsStepValueAccessor],
  imports: [
    AlertComponent,
    CheckboxComponent,
    CheckboxDescription,
    CommonModule,
    FormGroupComponent,
    I18NextPipe,
    InputComponent,
    NgbTooltipModule,
    RadioComponent,
    ReactiveFormsModule,
    SelectComponent
  ]
})
export class PrePostActionsStepComponent extends StepBase<PrePostActionsStepValue> implements OnInit, OnChanges {
  mainService: RemoteManagementWizardsService;

  public readonly alertType = MbsPopupType;

  @Input() ChainPlans = [];

  public readonly elementsSelector = {
    name: {
      isRDModeAlert: 'isRDModeAlert',
      allowPrePostActionsBlock: 'allowPrePostActionsBlock',
      allowPrePostActionsSecBlock: 'allowPrePostActionsSecBlock',
      notUnixNotRestore: 'notUnixNotRestore'
    }
  };

  constructor() {
    const mainService = inject(RemoteManagementWizardsService);

    super();

    this.mainService = mainService;
  }

  ngOnInit(): void {
    this.initForm();
  }

  initForm(): void {
    this.stepForm = new UntypedFormGroup({
      executeCommandBeforeBackup: new FormControl(false),
      preAction: new FormControl('', [Validators.required, Validators.minLength(2)]),
      exitOnPreActionFail: new FormControl(true),
      executeCommandAfterBackupCompletion: new FormControl(false),
      postAction: new FormControl('', [Validators.required, Validators.minLength(2)]),
      executePostActionOnSuccess: new FormControl(true),
      enableBackupChain: new FormControl(false),
      backupChainNextPlan: new FormControl('', [Validators.required]),
      executeChainPlanOnSuccess: new FormControl(false),
      ForceFullNextPlan: new FormControl(false)
    });

    this.initFormEvents();
  }

  onStepFormChange(value: PrePostActionsStepValue): void {
    if (this.stepForm.dirty || this.stepForm.touched) this.value = { ...value, valid: this.stepForm.valid };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.stepForm && changes.ChainPlans?.currentValue?.length) {
      const currentValue = this.stepForm.get('backupChainNextPlan').value;

      if (!currentValue) {
        return this.stepForm.get('backupChainNextPlan').patchValue(changes.ChainPlans.currentValue[0].value);
      }

      if (this.ChainPlans.some((p) => p.value === currentValue)) return;

      this.stepForm.get('backupChainNextPlan').patchValue('');
    }
  }

  updateForm(value: PrePostActionsStepValue): void {
    this.stepForm.reset(value);

    this.beforeBackupChangeHandler(value.executeCommandBeforeBackup);
    this.afterBackupChangeHandler(value.executeCommandAfterBackupCompletion);
    this.backupChainChangeHandler(value.enableBackupChain);

    if (this.ChainPlans && this.ChainPlans.length && !value.enableBackupChain && !value.backupChainNextPlan)
      this.stepForm.get('backupChainNextPlan').patchValue(this.ChainPlans[0].value);
  }

  beforeBackupChangeHandler(event): void {
    this.toggleFormControls(['preAction', 'exitOnPreActionFail'], event);
  }

  afterBackupChangeHandler(event): void {
    this.toggleFormControls(['postAction', 'executePostActionOnSuccess'], event);
  }

  backupChainChangeHandler(event): void {
    this.toggleFormControls(['backupChainNextPlan', 'executeChainPlanOnSuccess', 'ForceFullNextPlan'], event);
  }
}
